import React from 'react';
import { Snackbar, Box, Typography, IconButton, alpha, CircularProgress, Fade } from '@mui/material';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VideoCallOutlinedIcon from '@mui/icons-material/VideoCallOutlined';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';

const googleIcon = '';
const zoomIcon = '';

export default function NotificationSnackbar({
  open,
  onClose,
  isExpanded,
  isScrolledDown,
  notificationAnimState,
  setNotificationAnimState,
  activeAlert,
  profileCompletion = { percentage: 0 },
  upcomingClass,
  subscription,
  rightClearance = 80,
  openCmPayment,
  setLockedAlertOpen,
  setLockPopupOpen,
  handleProfileClick,
  setIsAlertOpen,
  navigate,
  activeNotification,
}) {
  return (
    <Snackbar
      open={open}
      TransitionComponent={Fade}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        top: '16px !important',
        ...(notificationAnimState === 'dot' ? {
          // In dot state: position at the exact horizontal center of the Teacher Header
          left: {
            xs: '50% !important',
            sm: '50% !important',
            md: isExpanded
              ? 'calc(300px + (100vw - 300px - 10px) / 2) !important'
              : 'calc(86px + (100vw - 86px - 10px) / 2) !important',
          },
          right: {
            xs: 'auto !important',
            sm: 'auto !important',
            md: 'auto !important',
          },
          transform: {
            xs: isScrolledDown ? 'translateX(-50%) scale(0.9) translateY(-10px) !important' : 'translateX(-50%) scale(1) translateY(0) !important',
            sm: isScrolledDown ? 'translateX(-50%) scale(0.9) translateY(-10px) !important' : 'translateX(-50%) scale(1) translateY(0) !important',
            md: isScrolledDown ? 'translateX(-50%) scale(0.95) translateY(-10px) !important' : 'translateX(-50%) scale(1) translateY(0) !important',
          },
          width: 'auto',
        } : {
          // In expanded state: stretch across to match PageHeader boundaries exactly
          left: 'calc(var(--sidebar-width) + 16px) !important',
          right: '16px !important',
          transform: {
            xs: isScrolledDown ? 'scale(0.9) translateY(-10px) !important' : 'scale(1) translateY(0) !important',
            sm: isScrolledDown ? 'scale(0.9) translateY(-10px) !important' : 'scale(1) translateY(0) !important',
            md: isScrolledDown ? 'scale(0.95) translateY(-10px) !important' : 'scale(1) translateY(0) !important',
          },
          width: 'auto',
        }),
        zIndex: 1290,
        // Only transition layout properties for dot↔expanded morph, NOT on initial open
        transition: notificationAnimState === 'expanded'
          ? 'left 0.4s cubic-bezier(0.16, 1, 0.3, 1), right 0.4s cubic-bezier(0.16, 1, 0.3, 1), transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease'
          : 'opacity 0.3s ease',
        opacity: isScrolledDown ? 0 : 1,
        pointerEvents: isScrolledDown ? 'none' : 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'visible !important',
        height: {
          xs: 'auto !important',
          md: '64px !important',
        },
        // Force overflow visible on all MUI internal wrappers so raindrop is visible above
        '& > *': {
          overflow: 'visible !important',
        },
        '& .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
          overflow: 'visible !important',
        },
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'visible !important',
          animation: notificationAnimState === 'dot' ? 'samsung-raindrop 1s cubic-bezier(0.22, 1, 0.36, 1) forwards' : 'none',
        }}
      >
        <Box
          className={`samsung-notif-container state-${notificationAnimState} alert-${activeAlert}`}
          sx={{
            background: notificationAnimState === 'expanded' ? '#FFFFFF !important' : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: notificationAnimState === 'expanded' ? 'none !important' : 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: notificationAnimState === 'expanded' ? 'none !important' : 'blur(20px) saturate(180%)',
            borderRadius: '16px',
            boxShadow: '0 12px 32px 0 rgba(31, 38, 135, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)',
            p: notificationAnimState === 'expanded' ? { xs: '8px 12px', md: '4px 24px' } : '8px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            border: '1px solid rgba(0, 0, 0, 0.08)',
            width: '100%',
            maxWidth: notificationAnimState === 'expanded' ? 'none !important' : '320px',
            minWidth: '260px',
            height: notificationAnimState === 'expanded' ? '64px !important' : 'auto',
            minHeight: notificationAnimState === 'expanded' ? '64px !important' : 'auto',
            transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            transformOrigin: 'top center',
            '&:hover': {
              cursor: activeAlert === 'profile' ? 'pointer' : 'default'
            }
          }}
          onClick={(e) => {
            if (notificationAnimState === 'dot') {
              e.stopPropagation();
              setNotificationAnimState('expanded');
            } else if (activeAlert === 'profile') {
              handleProfileClick();
            }
          }}
        >
          {/* Unified Dynamic Graphic Section */}
          <Box
            className="samsung-notif-graphic"
            sx={{
              position: 'relative',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: 36,
              height: 36,
              flexShrink: 0,
              alignSelf: 'center'
            }}
          >
            {/* The circular progress ring (only for profile) */}
            {activeAlert === 'profile' && (
              <>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size={36}
                  thickness={3}
                  sx={{ color: 'rgba(0,0,0,0.05)' }}
                />
                <CircularProgress
                  variant="determinate"
                  value={profileCompletion.percentage}
                  size={36}
                  thickness={3}
                  sx={{
                    color: '#FFA84A',
                    position: 'absolute',
                    left: 0, top: 0,
                    strokeLinecap: 'round',
                    filter: 'drop-shadow(0 0 4px rgba(255, 168, 74, 0.4))'
                  }}
                />
              </>
            )}

            {/* Upcoming class shows a simple light background circle with pulse */}
            {activeAlert === 'upcoming' && (
              <Box
                className={(() => {
                  if (!upcomingClass?.isOngoing) return "";
                  const link = upcomingClass?.liveLink || '';
                  if (link.includes('zoom.us')) return "pulse-zoom";
                  if (link.includes('google.com/meet') || link.includes('meet.google.com')) return "pulse-google";
                  return "pulse-zoom"; // Fallback to blue
                })()}
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: (() => {
                    if (!upcomingClass?.isOngoing) return 'rgba(226, 232, 240, 0.5)';
                    const link = upcomingClass?.liveLink || '';
                    if (link.includes('zoom.us')) return 'rgba(45, 140, 255, 0.1)';
                    if (link.includes('google.com/meet') || link.includes('meet.google.com')) return 'rgba(52, 168, 83, 0.1)';
                    return 'rgba(45, 140, 255, 0.1)';
                  })(),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
              />
            )}

            {/* Locked alert uses a square icon box */}
            {activeAlert === 'locked' && (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  bgcolor: alpha('#FF502D', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FF502D',
                  boxShadow: 'inset 0 0 10px rgba(255, 80, 45, 0.05)'
                }}
              >
                <LockRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
            )}

            {/* Exam alert uses a bell icon box */}
            {activeAlert === 'exam' && (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  bgcolor: alpha('#FF502D', 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FF502D',
                  boxShadow: 'inset 0 0 10px rgba(255, 80, 45, 0.05)'
                }}
              >
                <NotificationsNoneRoundedIcon sx={{ fontSize: 18 }} />
              </Box>
            )}

            {/* Inner Content for Progress Rings */}
            <Box
              sx={{
                top: 0, left: 0, bottom: 0, right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {activeAlert === 'profile' && (
                <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: '#1F2937', fontFamily: 'Poppins' }}>
                  {`${Math.round(profileCompletion.percentage)}%`}
                </Typography>
              )}
              {activeAlert === 'upcoming' && (
                (() => {
                  const link = upcomingClass?.liveLink || '';
                  if (link.includes('zoom.us')) {
                    return <Box component="img" src={zoomIcon} sx={{ width: 24, height: 24, zIndex: 2, display: 'block' }} />;
                  }
                  if (link.includes('google.com/meet') || link.includes('meet.google.com')) {
                    return <Box component="img" src={googleIcon} sx={{ width: 24, height: 24, zIndex: 2, display: 'block' }} />;
                  }
                  return <VideoCallOutlinedIcon sx={{ color: '#FF502D', fontSize: 20, zIndex: 2 }} />;
                })()
              )}
            </Box>
          </Box>

          {/* Unified Text and Action Section */}
          <Box
            className="samsung-notif-text"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              display: notificationAnimState === 'expanded' ? 'flex' : 'none',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: { xs: 1, md: 2 }
            }}
          >
            {/* Left side: Stacked Title & Subtitle */}
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flexGrow: 1 }}>
              <Typography sx={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '0.78rem', color: '#1F2937', lineHeight: 1.2 }}>
                {activeAlert === 'locked' && (
                  subscription?.status === 'grace' ? "Subscription Grace Period" : "Class Management Locked"
                )}
                {activeAlert === 'profile' && "Profile Incomplete"}
                {activeAlert === 'upcoming' && (upcomingClass?.isOngoing ? "Your class is live now!" : `Upcoming class at ${upcomingClass?.startTime?.substring(0, 5)}`)}
                {activeAlert === 'exam' && activeNotification?.title}
              </Typography>
              <Typography
                noWrap={activeAlert === 'upcoming'}
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: '0.66rem',
                  color: '#4B5563',
                  mt: 0.15,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: activeAlert === 'upcoming' ? 'nowrap' : 'normal'
                }}
              >
                {activeAlert === 'locked' && (
                  subscription?.status === 'grace'
                    ? `Your subscription has failed billing. You have ${subscription?.daysUntilGraceExpiry ?? 7} days remaining to pay.`
                    : subscription?.status === 'deleted'
                      ? 'Your Class Management subscription is deleted. Renew to restore access.'
                      : "Your Class Management subscription is locked. Renew to regain access."
                )}
                {activeAlert === 'profile' && "Help students find you and improve AI results by completing your profile."}
                {activeAlert === 'upcoming' && `${upcomingClass?.title} • ${upcomingClass?.date === new Date().toISOString().split('T')[0] ? 'Today' : upcomingClass?.date}`}
                {activeAlert === 'exam' && activeNotification?.description}
              </Typography>
            </Box>

            {/* Right side: Action Link */}
            {(activeAlert !== 'exam' || ['exam_started', 'EXAM_ASSIGNED', 'exam_published'].includes(activeNotification?.type)) && (
              <Typography
                onClick={(e) => {
                  e.stopPropagation();
                  if (activeAlert === 'locked') {
                    if (subscription?.status === 'grace' || subscription?.status === 'locked') {
                      openCmPayment({
                        flow: 'renew',
                        showPromo: false,
                        backToLocked: subscription?.status === 'locked',
                      });
                    } else if (subscription?.status === 'deleted') {
                      setLockedAlertOpen(true);
                    } else {
                      setLockPopupOpen(true);
                    }
                  }
                  else if (activeAlert === 'profile') handleProfileClick();
                  else if (activeAlert === 'upcoming') {
                    if (upcomingClass?.liveLink) window.open(upcomingClass.liveLink, '_blank');
                    else navigate('/classes');
                  }
                  else if (activeAlert === 'exam') {
                    onClose();
                    if (activeNotification?.type === 'exam_published') {
                      const targetId = activeNotification.data.classExamId || activeNotification.data.examId;
                      navigate(`/exam-manager/take-exam/${targetId}`);
                    } else if (activeNotification?.type === 'exam_started') {
                      const teacherId = activeNotification.data?.teacherId;
                      navigate(`/exam-manager/exams/${teacherId}`);
                    } else {
                      navigate(`/exam-manager/exam-summary/${activeNotification.data.classExamId}`);
                    }
                  }
                  setIsAlertOpen(false);
                }}
                sx={{
                  fontFamily: 'Poppins',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                  alignSelf: 'center',
                  transition: 'all 0.2s ease',
                  ...(activeAlert === 'exam' ? {
                    bgcolor: '#FF502D',
                    color: '#FFFFFF !important',
                    borderRadius: '10px',
                    padding: '8px 18px',
                    minHeight: '36px',
                    boxShadow: '0 4px 12px rgba(255, 80, 45, 0.2)',
                    '&:hover': {
                      bgcolor: '#E04324',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 16px rgba(255, 80, 45, 0.3)',
                      textDecoration: 'none'
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    }
                  } : {
                    color: activeAlert === 'upcoming' ? '#0070AC' : (activeAlert === 'locked' ? '#FF502D' : '#0070AC'),
                    '&:hover': { textDecoration: 'underline' }
                  })
                }}
              >
                {activeAlert === 'locked' && (
                  subscription?.status === 'grace' ? "Renew Now" : "Unlock Now"
                )}
                {activeAlert === 'profile' && "Complete Now"}
                {activeAlert === 'upcoming' && (upcomingClass?.liveLink ? (upcomingClass.isOngoing ? "Join Meeting" : "Start Meeting") : "Add Link")}
                {activeAlert === 'exam' && (activeNotification?.type === 'exam_published' ? 'Take Exam' : 'View Exam')}
              </Typography>
            )}
          </Box>

          <IconButton
            className="samsung-notif-close"
            size="small"
            onClick={(e) => { e.stopPropagation(); setIsAlertOpen(false); }}
            sx={{
              alignSelf: 'center',
              color: '#9CA3AF',
              ml: 0.5,
              display: notificationAnimState === 'expanded' ? 'inline-flex' : 'none'
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Snackbar>
  );
}
